import cn from "classnames"
import { useCallback, useRef, useState } from "react"

import { Flex } from "@/atoms"
import { useScrollLock } from "@/hooks/useScrollLock"
import { RichTextEditor } from "@/molecules/RichTextEditor"
import { DynamicTagButton } from "./components/DynamicTagButton"
import { DynamicTagsMenu } from "./components/DynamicTagsMenu"
import { useDropdownPosition } from "./hooks"
import { ClosedTagNode } from "./nodes/ClosedTagNode"
import { DynamicTagNode } from "./nodes/DynamicTagNode"
import { DynamicTagPlugin } from "./plugins/DynamicTagPlugin"
import { InitialHtmlPlugin } from "./plugins/InitialHtmlPlugin"
import { SingleLinePlugin } from "./plugins/SingleLinePlugin"

import styles from "./DynamicTagsField.module.scss"

import type { DynamicTag } from "@/api/useWorkflow.types"
import type { DropdownState, DynamicTagPayload, DynamicTagsFieldProps } from "."

export const DynamicTagsField = (props: DynamicTagsFieldProps) => {
  const {
    onChange,
    html = "",
    disabled = false,
    toolbar = true,
    previewMode = false,
    type = "textarea"
  } = props
  const isDisabled = disabled || previewMode
  const getEmptyState = (): DropdownState => ({
    visible: false,
    rect: null,
    nodeKey: null
  })
  const [dropdownState, setDropdownState] =
    useState<DropdownState>(getEmptyState())
  const [search, setSearch] = useState("")
  const closeMenu = useRef(() => {
    setDropdownState(getEmptyState())
    setSearch("")
  }).current
  const openMenu = useRef((payload: Partial<DropdownState>) => {
    setDropdownState((prev) => ({ ...prev, ...payload, visible: true }))
    setSearch("")
  }).current
  const onInput = useCallback(
    (payload: DynamicTagPayload) => {
      if (!dropdownState.visible || !dropdownState.nodeKey) return

      setSearch(payload.aggregatedText.toLowerCase())
    },
    [dropdownState.visible, dropdownState.nodeKey]
  )

  const isInput = type === "input"
  const availableTags = useRef<DynamicTag[]>()
  const keysTags = useRef<{ [x in string]: boolean }>()
  const scrollLockSelectors = useRef<string[]>()

  if (!availableTags.current)
    availableTags.current = isInput
      ? props.tags?.filter((tag) => tag.valueType === "text")
      : props.tags

  if (!keysTags.current && availableTags.current) {
    keysTags.current = availableTags.current.reduce(
      (acc, tag) => {
        acc[tag.key] = true

        return acc
      },
      {} as { [x in string]: boolean }
    )
  }

  if (!scrollLockSelectors.current)
    scrollLockSelectors.current = [
      ".ant-modal-wrap",
      ".ant-modal-body",
      ".editor-textarea-input-lock-scroll",
      `.${styles.editorInputInput}`
    ]

  useScrollLock(dropdownState.visible, scrollLockSelectors.current)

  const position = useDropdownPosition({ triggerRect: dropdownState.rect })

  const tags = availableTags.current?.filter(
    (tag) =>
      tag.key.toLowerCase().includes(search) ||
      tag.displayName.toLowerCase().includes(search)
  )

  const classes = isInput
    ? {
        inner: styles.editorInputInner,
        input: cn(
          styles.editorInputInput,
          disabled && styles.editorInputInputDisabled
        ),
        container: cn(styles.containerCommon, styles.editorInputContainer)
      }
    : {
        container: styles.containerCommon,
        elements: styles.elements,
        input: "editor-textarea-input-lock-scroll"
      }

  const replaceOnEnter = dropdownState.visible && !!tags?.length

  return (
    <div className={cn(isInput && styles.wrapperInput)}>
      <RichTextEditor
        noStyles
        html=""
        onChange={onChange}
        nodes={[DynamicTagNode, ClosedTagNode]}
        disabled={isDisabled}
        toolbar={type === "textarea" && toolbar}
        classes={classes}
        extraTools={
          !isInput && (
            <Flex flex={1} justify="end">
              <DynamicTagButton disabled={disabled} openMenu={openMenu} />
            </Flex>
          )
        }
      >
        <InitialHtmlPlugin html={html} />
        {isInput && (
          <>
            {!replaceOnEnter && <SingleLinePlugin />}
            <DynamicTagButton
              disabled={disabled}
              type="text"
              openMenu={openMenu}
            />
          </>
        )}
        <DynamicTagPlugin
          activeNodeKey={dropdownState.nodeKey}
          keysTags={keysTags.current}
          replaceOnEnter={replaceOnEnter}
          onTagCreate={openMenu}
          onTagInput={onInput}
          onTagDestroyed={closeMenu}
        >
          {({ replaceWithClosedTag }) => {
            const showMenu = dropdownState.visible && position
            const {
              top,
              bottom,
              left,
              menuWidth: w,
              menuHeight: h
            } = position || {}

            return (
              showMenu && (
                <DynamicTagsMenu
                  style={{ top, bottom, left, minWidth: w, maxHeight: h }}
                  tags={tags}
                  activeNodeKey={dropdownState.nodeKey}
                  replaceWithClosedTag={replaceWithClosedTag}
                  close={closeMenu}
                />
              )
            )
          }}
        </DynamicTagPlugin>
      </RichTextEditor>
    </div>
  )
}
